/**
 * VIVANA × IT GEEKS — One-Time Pack Logic (pure, DOM-free)
 *
 * Decides whether a /cart/add should be rewritten into a one-time "pack" of the
 * hidden bundle product, computes the multiplied quantity, and builds the line
 * item properties. No DOM, no window access beyond the dual-export shim, so it is
 * unit-testable under plain `node` (see tests/vivana-pack-logic.test.js).
 *
 * Loaded as a classic script BEFORE vivana-subscription-cart.js so
 * window.VivanaPackLogic is available to the fetch interceptor and the block
 * controller.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.VivanaPackLogic = api;
})(typeof self !== 'undefined' ? self : this, function () {
  /**
   * @param {string|number} variantId  variant id on the add body (the main product)
   * @param {string|number|null} planId  selling_plan on the add body ('' when one-time)
   * @param {object|null} packState  window.VivanaPackState set by the block controller
   * @returns {{bundleVariantId:number, label:string, mainVariantId:string,
   *   mainUrl:string, mode:('three'|'six')} | null}
   *
   * The bundle variant is resolved from `packState.variantMap` keyed by the MAIN
   * variant id being added (i.e. the currently selected flavor), so it is always
   * correct regardless of when/where the flavor was chosen. Falls back to
   * `packState.bundleVariantId` for single-variant bundles (no per-flavor map).
   */
  function resolvePackTarget(variantId, planId, packState) {
    if (!packState) return null;
    // A selling plan means the subscription path owns this add — never a pack.
    if (planId != null && String(planId) !== '') return null;
    const mode = packState.mode;
    if (mode !== 'three' && mode !== 'six') return null;

    const ids = Array.isArray(packState.mainVariantIds)
      ? packState.mainVariantIds
      : packState.mainVariantId != null
        ? [packState.mainVariantId]
        : [];
    if (!ids.map(String).includes(String(variantId))) return null;

    // Resolve the bundle variant that matches THIS main variant (flavor). The map
    // is keyed by main variant id; fall back to the default single bundle variant.
    let bundleVariantId = null;
    const map = packState.variantMap;
    if (map && map[String(variantId)] != null) {
      bundleVariantId = map[String(variantId)];
    } else {
      bundleVariantId = packState.bundleVariantId;
    }
    if (!bundleVariantId) return null;

    return {
      bundleVariantId: Number(bundleVariantId),
      label: packState.label || '',
      mainVariantId: String(variantId),
      mainUrl: packState.mainUrl || '',
      mode: mode,
    };
  }

  /**
   * Final bundle quantity to add. Quantity is expressed in BUNDLE UNITS (the
   * step-2 PDP selector already provides the doubled count for Six-Month), so we
   * add it as-is. Safety net for Six-Month: never add an odd or < 2 count — a
   * "Six-Month Pack" is a whole 6-month unit = 2 bundles.
   * @param {object} target  from resolvePackTarget
   * @param {string|number} formQty
   */
  function packQuantity(target, formQty) {
    let q = parseInt(formQty, 10);
    if (!Number.isFinite(q) || q < 1) q = 1;
    if (target && target.mode === 'six') {
      if (q < 2) q = 2;
      if (q % 2 !== 0) q += 1; // round an odd count up to a whole 6-month pack
    }
    return q;
  }

  /**
   * Build the line-item properties for a pack add.
   * @param {object} target  from resolvePackTarget
   * @param {{packLabel:string, bundleSwap:string, bundleSource:string, bundleSourceUrl:string, packMode:string}} keys
   */
  function packProperties(target, keys) {
    const props = {};
    if (target.label) props[keys.packLabel] = target.label; // visible label
    props[keys.bundleSwap] = 'true'; // excluded from DOI/swap; cart-link handling
    props[keys.bundleSource] = String(target.mainVariantId);
    if (target.mainUrl) props[keys.bundleSourceUrl] = String(target.mainUrl);
    if (keys.packMode && target.mode) props[keys.packMode] = target.mode; // hidden: cart step-by-2
    return props;
  }

  return { resolvePackTarget, packQuantity, packProperties };
});
